import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { Form, Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../../components/Button';
import SingleSelect from '../../../components/SingleSelect';
import LoadingSpinner from '../../../components/LoadingSpinner';

//ICONS
import { ReactComponent as MailSentIcon } from '../../../assets/svg/mail-sent.svg';

//TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { AccountLinkAccountsCommand } from '../../../api/models';

interface InviteToLinkModalTypes {
	closeModal?: closeModalType;
	update?: () => void;
}

const InviteToLinkModal: React.FC<InviteToLinkModalTypes> = ({ closeModal, update }) => {
	// Get all Relationship Types from Redux store
	const relationship = useAppSelector((state) => state?.lists.relationshipTypes);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [emailSent, setEmailSent] = React.useState<boolean>(false);

	const [childId, setChildId] = React.useState<string>('');

	const submitInviteRequest = async (values: any) => {
		setIsLoading(true);

		const response = await apibridge.postAccountLinkAccounts({
			relationshipType: values.relationshipType,
			childAwareId: values.childAwareId
		});

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setEmailSent(true);

			setChildId(values.childAwareId);
		}
	};

	const close = () => {
		closeModal && closeModal();
		update && update();
	};

	const initalFormData: AccountLinkAccountsCommand = {
		relationshipType: undefined,
		childAwareId: undefined
	};

	const formValidation = Yup.object({
		relationshipType: Yup.string().required('Required'),
		childAwareId: Yup.number().required('Required')
	});

	return (
		<div className="request-status">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik initialValues={initalFormData} validationSchema={formValidation} onSubmit={submitInviteRequest}>
								{(formik) => {
									const resetFields = () => {
										setEmailSent(false);
										formik.setFieldValue('relationshipType', undefined);
										formik.setFieldValue('childAwareId', undefined);
									};
									return (
										<Form translate="yes">
											{!emailSent ? (
												<>
													<MailSentIcon className="center-icon" />

													<p className="request-text">
														Invite a family member to link your account.
														<br /> Enter either their Child<strong>Aware</strong> ID below.
													</p>

													<div className="form-group">
														<label htmlFor="relationshipType">Relationship to child</label>

														<Field
															name="relationshipType"
															children={() => (
																<SingleSelect
																	onChange={(val: string) => formik.setFieldValue('relationshipType', val)}
																	name="relationshipType"
																	options={relationship}
																/>
															)}
														/>

														<ErrorMessage name="relationshipType">
															{(error) => <div className="form-validation-error">{error}</div>}
														</ErrorMessage>
													</div>

													<div className="form-group">
														<label htmlFor="childAwareId">Recipients ChildAware ID</label>

														<Field
															name="childAwareId"
															className="form-control"
															onChange={formik.handleChange}
															placeholder="E.g. 1234567"
															type="text"
														/>

														<ErrorMessage name="childAwareId">
															{(error) => <div className="form-validation-error">{error}</div>}
														</ErrorMessage>
													</div>

													<Button type="submit" className="btn btn-primary full-width mbtom-16">
														Send
													</Button>
												</>
											) : (
												<>
													<MailSentIcon className="center-icon" />

													<p className="request-text">
														Your invitation has been successfully sent to:
														<br />
														<strong>ChildAware ID #{childId}</strong>
													</p>

													<Button
														type="button"
														onClick={() => resetFields()}
														className="btn btn-primary full-width mbtom-16"
													>
														Send another link invite
													</Button>

													<Button type="button" onClick={close} className="btn btn-secondary full-width mbtom-16">
														Close
													</Button>
												</>
											)}
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default InviteToLinkModal;
